import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { useGetBankAccountsQuery, useDeleteBankAccountMutation } from "./api/bankAccountApi";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditBankAccount from "./components/AddEditBankAccount";
import BankAccountDetailDialog from "./components/BankAccountDetail";

const BankAccountManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [detailOpen, setDetailOpen] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const navigate = useNavigate();

  const {
    data: accounts,
    isLoading,
    isError,
    isFetching
  } = useGetBankAccountsQuery();

  const [deleteBankAccount] = useDeleteBankAccountMutation();

  const columns: Array<MRT_ColumnDef<BankAccount>> = [
    {
      header: "Bank Name",
      accessorKey: "bank_name",
      size: 150,
    },
    {
      header: "Account Number",
      accessorKey: "account_number",
      size: 150,
    },
    {
      header: "QR Code",
      accessorKey: "qr_code",
      size: 120,
      Cell({ row: { original } }) {
        return original.qr_code ? (
          <img
            src={original.qr_code}
            alt="Bank QR Code"
            width={60}
            style={{ borderRadius: 6 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No QR
          </Typography>
        );
      },
    },
  ];

  return (
    <Box>
      <DataTable<BankAccount>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Bank Accounts</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${accounts?.data?.length ?? 0} Accounts`}
              />
            </Box>
            <Button
              onClick={() => {
                setSelectedAccount(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
            >
              Add New Account
            </Button>
          </Box>
        }
        list={accounts?.data ?? []}
        count={accounts?.data?.length ?? 0}
        columns={columns}
        getRowId={(row) => row.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAccount(row);
                setDetailOpen(true);
              }}
            >
              <RemoveRedEye />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAccount(row);
                setOpenConfirmModal(true);
              }}
            >
              <Delete color="error" />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAccount(row);
                setOpenDialog(true);
              }}
            >
              <Edit color="primary" />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={(row) => {
          setSelectedAccount(row);
          setDetailOpen(true);
        }} 
      />

      <AddEditBankAccount
        open={openDialog}
        account={selectedAccount}
        setModalOpen={setOpenDialog}
      />
      {selectedAccount && (
        <BankAccountDetailDialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          account={selectedAccount}
        />)}

      {selectedAccount && (
        <ConfirmModal
          type="warning"
          title="Delete Bank Account"
          message={`Are you sure you want to delete ${selectedAccount.bank_name} account (${selectedAccount.account_number})?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={() => {
            deleteBankAccount(selectedAccount.id.toString());
            setOpenConfirmModal(false);
            setSelectedAccount(null);
          }}
        />
      )}
    </Box>
  );
};

export default BankAccountManager;